import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '@/lib/firebase';
import { useLanguage } from '@/contexts/LanguageContext';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext'; // NOUVEAU
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle2, Loader2, Star } from 'lucide-react';
import { PARTNER_PLANS, PARTNER_ADDON_AMOUNT_USD, PARTNER_ADDON_LABELS, type PartnerPlanKey, type PartnerAddonSlug } from '@/lib/partner-billing';

const ADDON_SLUGS: PartnerAddonSlug[] = ['business_law', 'immigration_law', 'notary', 'cpa', 'tax_advisor'];

export default function PartnerPlans() {
  const { language } = useLanguage();
  const { user } = useAuth(); // NOUVEAU
  const { toast } = useToast();
  const navigate = useNavigate();
  const lang = language as 'en' | 'fr';
  const [selectedPlan, setSelectedPlan] = useState<PartnerPlanKey>('pro');
  const [selectedAddons, setSelectedAddons] = useState<PartnerAddonSlug[]>([]);
  const [loading, setLoading] = useState(false);

  const plan = PARTNER_PLANS[selectedPlan];
  const addonTotal = selectedAddons.length * PARTNER_ADDON_AMOUNT_USD;
  const monthlyTotal = plan.price + addonTotal;

  // NOUVEAU — précharge la sélection sauvegardée quand l'utilisateur revient
  // ici après vérification Didit (compte déjà authentifié)
  useEffect(() => {
    if (!user) return;
    try {
      const saved = localStorage.getItem('selectedPartnerPlan');
      if (saved) {
        const { plan: savedPlan, addons: savedAddons } = JSON.parse(saved);
        if (savedPlan && PARTNER_PLANS[savedPlan as PartnerPlanKey]) {
          setSelectedPlan(savedPlan as PartnerPlanKey);
        }
        if (Array.isArray(savedAddons)) {
          setSelectedAddons(savedAddons as PartnerAddonSlug[]);
        }
      }
    } catch {
      /* ignore */
    }
  }, [user]);

  const toggleAddon = (slug: PartnerAddonSlug) => {
    setSelectedAddons(prev =>
      prev.includes(slug) ? prev.filter(s => s !== slug) : [...prev, slug]
    );
  };

  // MODIFIÉ — deux chemins possibles :
  // 1. Pas encore de compte → sauvegarde le choix, redirige vers le register
  // 2. Compte déjà authentifié (retour post-vérification Didit, ou upgrade
  //    de plan) → lance directement le checkout Stripe
  const handleCheckout = async () => {
    localStorage.setItem('selectedPartnerPlan', JSON.stringify({
      plan: selectedPlan,
      addons: selectedAddons,
      priceId: PARTNER_PLANS[selectedPlan].priceId,
    }));

    if (!user) {
      navigate('/register/partner');
      return;
    }

    setLoading(true);
    try {
      const idToken = await auth.currentUser?.getIdToken(true);
if (!idToken) throw new Error('Authentication token unavailable. Please sign in again.');
      const res = await fetch(`${import.meta.env.VITE_API_URL}/payments/partner-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          addons: selectedAddons,
          baseTier: selectedPlan,
          origin: window.location.origin,
          successPath: `/payment-success?role=partner&step=partner_subscription&tier=partner_${selectedPlan}`,
cancelPath: '/partner/plans?canceled=true',
        }),
      });

      if (!res.ok) {
        const errText = await res.text().catch(() => '');
        throw new Error(errText || `Checkout failed (${res.status})`);
      }

      const data = await res.json();
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      throw new Error('Stripe did not return a checkout URL.');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      toast({
        title: lang === 'fr' ? 'Erreur' : 'Error',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-10">
          <h1 className="text-3xl font-bold text-[#1D314D] mb-2">
            {lang === 'fr' ? 'Choisissez votre forfait' : 'Choose your plan'}
          </h1>
          <p className="text-muted-foreground">
            {lang === 'fr'
              ? 'Accédez à la plateforme AcquizGroup en tant que Partenaire Interne'
              : 'Access the AcquizGroup platform as an Internal Partner'}
          </p>
        </div>

        {/* Plans */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {(Object.entries(PARTNER_PLANS) as [PartnerPlanKey, typeof PARTNER_PLANS[PartnerPlanKey]][]).map(([key, p]) => (
            <Card
              key={key}
              onClick={() => setSelectedPlan(key)}
              className={`cursor-pointer transition-all ${
                selectedPlan === key
                  ? 'border-2 border-[#CEAB65] shadow-lg'
                  : 'border border-gray-200 hover:border-[#CEAB65]/50'
              }`}
            >
              <CardHeader className="pb-2">
                {key === 'pro' && (
                  <div className="flex justify-center mb-2">
                    <span className="bg-[#CEAB65] text-[#1D314D] text-xs font-bold px-3 py-1 rounded-full">
                      {lang === 'fr' ? 'Populaire' : 'Popular'}
                    </span>
                  </div>
                )}
                {key === 'elite' && (
                  <div className="flex justify-center mb-2">
                    <span className="bg-[#1D314D] text-[#CEAB65] text-xs font-bold px-3 py-1 rounded-full">
                      {lang === 'fr' ? 'Meilleure valeur' : 'Best Value'}
                    </span>
                  </div>
                )}
                <CardTitle className="text-center text-[#1D314D]">{p.name[lang]}</CardTitle>
                <div className="text-center">
                  <span className="text-3xl font-bold text-[#1D314D]">${p.price}</span>
                  <span className="text-muted-foreground">/mo</span>
                  <div className="text-xs text-muted-foreground line-through">${p.regularPrice}/mo</div>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {p.features[lang].map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <CheckCircle2 className="h-4 w-4 text-[#CEAB65] shrink-0 mt-0.5" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Additional Roles */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-[#1D314D] text-base">
              {lang === 'fr' ? `Rôles supplémentaires (+$${PARTNER_ADDON_AMOUNT_USD}/mois chacun)` : `Additional roles (+$${PARTNER_ADDON_AMOUNT_USD}/mo each)`}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-5 gap-3">
              {ADDON_SLUGS.map(slug => (
                <label key={slug} className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all ${
                  selectedAddons.includes(slug) ? 'border-[#CEAB65] bg-[#CEAB65]/10' : 'border-gray-200'
                }`}>
                  <input
                    type="checkbox"
                    checked={selectedAddons.includes(slug)}
                    onChange={() => toggleAddon(slug)}
                    className="accent-[#CEAB65]"
                  />
                  <span className="text-sm font-medium">{PARTNER_ADDON_LABELS[slug]}</span>
                </label>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Summary + CTA */}
        <Card className="border-[#1D314D]">
          <CardContent className="py-4 flex items-center justify-between">
            <div>
              <p className="font-semibold text-[#1D314D]">
                {lang === 'fr' ? 'Total mensuel' : 'Monthly total'}
              </p>
              <p className="text-2xl font-bold text-[#1D314D]">
                ${monthlyTotal}<span className="text-sm font-normal text-muted-foreground">/mo</span>
              </p>
              {selectedAddons.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  ${plan.price} + ${addonTotal} {lang === 'fr' ? 'rôles supp.' : 'add-on roles'}
                </p>
              )}
            </div>
            <Button
              onClick={handleCheckout}
              disabled={loading}
              className="bg-[#CEAB65] text-[#1D314D] font-bold px-8"
            >
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {user
                ? (lang === 'fr' ? 'Procéder au paiement' : 'Proceed to payment')
                : (lang === 'fr' ? "Continuer vers l'inscription" : 'Continue to registration')}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}